/**
 * Pure grouping of long-lived "in-progress" cockpit cards by business type.
 *
 * The cockpit is a DECISION queue, not a TRACKING board. A manager with 100
 * properties accumulates hundreds of cards that are merely *in progress* —
 * interventions dispatched and awaiting the provider, indexations waiting for
 * the official index to publish, notices running their legal clock. Those
 * don't each deserve a row: they belong in a per-type "En cours" tile that
 * shows a count + a sub-state breakdown, and links into the dedicated panel.
 *
 * The cards that genuinely need a decision NOW (`pending`) still surface
 * individually in the flux above — AND are counted here under `attention`, so
 * the tile's total stays a reassuring "everything of this type" figure (see
 * the product decision: "Flux + compté dans le groupe").
 *
 * Classification is driven purely by `status` (+ `intent`): no workflow-step
 * introspection is needed because the backend already parks a card on
 * `waiting_external` while it waits on an external condition and flips it back
 * to `pending` the moment it becomes actionable (e.g. `reprojectIndexationCards`
 * un-parks an indexation once the index publishes). That keeps this helper
 * framework-free and trivially unit-testable.
 */

import type { ActionCard, CardIntent } from '@shared/definitions/action-card.model';
import { isSimpleSystemCard } from './system-card';

// View models moved to shared/definitions so shared cockpit-primitives can
// consume them without importing this feature. Re-exported for backward compat.
import type {
  CockpitGroupType,
  CockpitGroupSubState,
  CockpitGroupSubCount,
  CockpitGroup,
} from '@shared/definitions/cockpit-group.model';

export type {
  CockpitGroupType,
  CockpitGroupSubState,
  CockpitGroupSubCount,
  CockpitGroup,
};

/**
 * Intent → group mapping. Mirrors the existing `cardsByCategory` precedent.
 * Intentionally narrow: `REMINDER` (rent/charges arrears, deadlines) and
 * `LEGAL` (litigation) are NOT long-lived tracking families — their cards are
 * action-required and belong in the flux, not an "en cours" tile.
 */
const INTENT_TO_GROUP: Partial<Record<CardIntent, CockpitGroupType>> = {
  MAINTENANCE: 'interventions',
  INDEXATION: 'indexations',
  RENEWAL: 'notices',
  PAYMENT: 'payments',
};

/** Canonical display order of the tiles. */
export const COCKPIT_GROUP_ORDER: readonly CockpitGroupType[] = [
  'interventions',
  'indexations',
  'notices',
  'payments',
];

/** Canonical order of sub-state badges within a tile. */
const SUBSTATE_ORDER: readonly CockpitGroupSubState[] = [
  'attention',
  'inProgress',
  'scheduled',
  'snoozed',
];

/**
 * Map a card to its sub-state, or `null` when the card is not in a long-lived
 * in-progress lifecycle (terminal, failed, etc.) and so doesn't belong to any
 * tile.
 */
export function resolveGroupSubState(card: ActionCard): CockpitGroupSubState | null {
  switch (card.status) {
    case 'pending':
      return 'attention';
    case 'waiting_external':
      return 'inProgress';
    case 'scheduled_auto':
      return 'scheduled';
    case 'snoozed':
      return 'snoozed';
    default:
      return null;
  }
}

/** Map a card to its group type, or `null` when its intent isn't grouped. */
export function resolveGroupType(card: ActionCard): CockpitGroupType | null {
  return INTENT_TO_GROUP[card.classification.intent] ?? null;
}

/**
 * True when a card is a pre-linked **system reminder** that belongs to a grouped
 * family — so it should be represented ONLY by its KPI tile and pulled OUT of the
 * individual "to act" flux (the cockpit is a decision queue, not a list of every
 * unapproved rent reminder).
 *
 * Deliberately narrow, exactly matching the product rule "système ≠ discussion":
 * - {@link resolveGroupType} ≠ null  → the card has a family box to live in;
 * - {@link isSimpleSystemCard}       → it's a pre-linked reminder with no workflow
 *   and is NOT a human inbound email. So a genuine email discussion about a
 *   payment/indexation, and a dunning *escalation* (which carries a workflow),
 *   both stay as individual cards in the flux.
 *
 * Pure; the KPI counters ({@link buildInProgressGroups}) still count these cards,
 * so they are relocated, never lost.
 */
export function isGroupedSystemCard(card: ActionCard): boolean {
  return resolveGroupType(card) !== null && isSimpleSystemCard(card);
}

/**
 * Bucket all in-progress cards into per-type groups. Returns only non-empty
 * groups, in canonical tile order. Pure — no signals, no side effects.
 */
export function buildInProgressGroups(cards: readonly ActionCard[]): CockpitGroup[] {
  const byType = new Map<CockpitGroupType, { sub: Map<CockpitGroupSubState, number>; ids: string[]; attentionIds: string[] }>();

  for (const card of cards) {
    const type = resolveGroupType(card);
    if (!type) continue;
    const sub = resolveGroupSubState(card);
    if (!sub) continue;

    let bucket = byType.get(type);
    if (!bucket) {
      bucket = { sub: new Map(), ids: [], attentionIds: [] };
      byType.set(type, bucket);
    }
    bucket.sub.set(sub, (bucket.sub.get(sub) ?? 0) + 1);
    bucket.ids.push(card.id);
    if (sub === 'attention') bucket.attentionIds.push(card.id);
  }

  const groups: CockpitGroup[] = [];
  for (const type of COCKPIT_GROUP_ORDER) {
    const bucket = byType.get(type);
    if (!bucket) continue;

    const subCounts: CockpitGroupSubCount[] = SUBSTATE_ORDER
      .map((state) => ({ state, count: bucket.sub.get(state) ?? 0 }))
      .filter((s) => s.count > 0);

    const total = subCounts.reduce((n, s) => n + s.count, 0);

    groups.push({
      type,
      total,
      attentionCount: bucket.sub.get('attention') ?? 0,
      subCounts,
      cardIds: bucket.ids,
      attentionCardIds: bucket.attentionIds,
    });
  }

  return groups;
}
